import React, { useState, useEffect, useRef } from "react";
import Flatpickr from "react-flatpickr";
import moment from 'moment';
import Api from "../../../services/ApiService";
import { requestsDateFormat } from '../../../helpers/dateFormat';
import { formatTime, parseTime } from '../../../helpers/timeFormat';
import './WorkingHours.scss';

const WorkingHours = () => {
    const [date, setDate] = useState(new Date());
    const [isDayOff, setIsDayOff] = useState(false);
    const [isSickDay, setIsSickDay] = useState(false);
    const [loggedMins, setLoggedMins] = useState(0);
    const [requestLoggedMins, setRequestLoggedMins] = useState(0);
    const [totalRecordedMins, setTotalRecordedMins] = useState(0);
    const [totalRecordedDays, setTotalRecordedDays] = useState(0);
    const [totalRecordedMonth, setTotalRecordedMonth] = useState('');
    const [isTotalRecorded, setIsTotalRecorded] = useState(false);
    const formattedDefaultValue = moment(date).format("dddd, Do [of] MMMM YY");
    const isOffCheckBoxNeglected = useRef(false);
    const isSickCheckBoxNeglected = useRef(false);
    const isSliderNeglected = useRef(false);
    const totalRecordedHrs = totalRecordedMins / 60;
    
    const requestBody = {
        date: requestsDateFormat(date),
        minutes_logged: requestLoggedMins,
        is_day_off: isDayOff,
        is_sick_day: isSickDay,
    };

    const toggleOffCheckBox = () => {
        setIsDayOff(prev => !prev);
        isOffCheckBoxNeglected.current = true;
    };

    const toggleSickCheckBox = () => {
        setIsSickDay(prev => !prev);
        isSickCheckBoxNeglected.current = true;
    };

    const logWorkingHrs = (event) => {
        const minutes = parseInt(event.target.value, 10);
        setLoggedMins(minutes);
    };

    const handleDragEnd = () => {
        setRequestLoggedMins(loggedMins);
        isSliderNeglected.current = true;
    };

    const showTotalRecordedData = (data) => {
        setTotalRecordedMins(data?.month_total_minutes_logged);
        setTotalRecordedDays(data?.month_total_days_logged);
        setTotalRecordedMonth(moment(data?.date).format('MMMM'));
        setIsTotalRecorded(true);
    };
    const hideTotalRecordedData = () => {
        setTotalRecordedMins('');
        setTotalRecordedDays('');
        setTotalRecordedMonth('');
        setIsTotalRecorded(false);
    };
    
    useEffect(() => {
        window.scrollTo(0, 0);
        isOffCheckBoxNeglected.current = false;
        isSickCheckBoxNeglected.current = false;
        isSliderNeglected.current = false;
        return () => {
            isOffCheckBoxNeglected.current = true;
            isSickCheckBoxNeglected.current = true;
            isSliderNeglected.current = true;
        }
    }, []);

    useEffect(() => {
        const fetchData = async () => {
          try {
            const { status, data } = await Api.get(`workdays/${requestsDateFormat(date)}`);
            if (status === 200) {
                isOffCheckBoxNeglected.current = false;
                isSickCheckBoxNeglected.current = false;
                isSliderNeglected.current = false;
                setIsDayOff(data?.is_day_off);
                setIsSickDay(data?.is_sick_day);
                setLoggedMins(data?.minutes_logged);
                setRequestLoggedMins(data?.minutes_logged);
                showTotalRecordedData(data);
            } else {
                setIsDayOff(false);
                setIsSickDay(false);
                setLoggedMins(0);
                setRequestLoggedMins(0);
                hideTotalRecordedData();
            }
          } catch (error) {
            console.error(error);
          }
        };
    
        fetchData();
    }, [date]);

    useEffect(() => {
        if (isOffCheckBoxNeglected.current) {
            try {
                Api.post('workdays', { ...requestBody, is_day_off: isDayOff })
                    .then((response) => {
                        showTotalRecordedData(response.data);
                    });
            } catch (error) {
                hideTotalRecordedData();
                console.error(error);
            }
        }
    }, [isDayOff, isOffCheckBoxNeglected]);

    useEffect(() => {
        if (isSickCheckBoxNeglected.current) {
            try {
                Api.post('workdays', { ...requestBody, is_sick_day: isSickDay })
                    .then((response) => {
                        showTotalRecordedData(response.data);
                    });
            } catch (error) {
                hideTotalRecordedData();
                console.error(error);
            }
        }
    }, [isSickDay, isSickCheckBoxNeglected]);

    useEffect(() => {
        if (isSliderNeglected.current) {
            try {
                Api.post('workdays', { ...requestBody, minutes_logged: requestLoggedMins })
                    .then((response) => {
                        showTotalRecordedData(response.data);
                    });
            } catch (error) {
                hideTotalRecordedData();
                console.error(error);
            }
        }
    }, [requestLoggedMins, isSliderNeglected]);

    return (
        <div className="working-hours-container">
            <div className="working-hours-box">
                <div className="row date-of-working-day">
                    <span className="col-md-3 col-sm-12 date-label">Date</span>
                    <Flatpickr
                        className="col-md-9 col-sm-12 working-hours-date-picker"
                        options={{
                            dateFormat: "l, J \\of F 'y",
                            minDate: new Date(new Date().getFullYear(), 3, 15),
                            maxDate: new Date(new Date().getFullYear(), 9, 31),
                            disableMobile: "true",
                        }}
                        static={true}
                        placeholder="Pick a date"
                        defaultValue={formattedDefaultValue}
                        date={date}
                        onChange={(date) => {
                            isOffCheckBoxNeglected.current = false;
                            isSickCheckBoxNeglected.current = false;
                            isSliderNeglected.current = false;
                            setDate(date[0]);
                        }}
                    />
                    <span className="zwicon-calendar calendar-icon" />
                </div>
                <div className="row hours-slider">
                    <span className="col-md-3 col-sm-12 working-hrs-label">Working Hours (Without Break)</span>
                    <div className="col-md-9 col-sm-12 working-hrs-log">
                        <span className={`working-hrs-amount ${isDayOff || isSickDay ? 'grey-area' : ''}`}>
                            {formatTime(isSickDay || isDayOff ? '0' : loggedMins)}
                        </span>
                        <input
                            type="range"
                            className={`working-hrs-slider ${isDayOff || isSickDay ? 'grey-area' : ''}`}
                            min={parseTime('00:00')}
                            max={parseTime('18:00')}
                            step={15}
                            value={isSickDay || isDayOff ? '0' : loggedMins}
                            onInput={logWorkingHrs}
                            onMouseUp={handleDragEnd}
                            onTouchEnd={handleDragEnd}
                        />
                    </div>
                    <div className={`disabling-area ${isDayOff || isSickDay ? 'grey-area' : ''}`}></div>
                </div>
                {
                    isTotalRecorded && (
                        <div className="total-recorded">
                            (Total recorded in {totalRecordedMonth} {totalRecordedHrs} {totalRecordedHrs > 1 ? 'hrs' : 'hr'}
                            {' '} in {totalRecordedDays} {totalRecordedDays > 1 ? 'days' : 'day'})
                        </div>
                    )
                }
                <div className="row day-off-checkbox">
                    <div className={`col-9 switch-container ${isSickDay ? 'disabled' : ''}`}>
                        <div
                            className={`switch ${isDayOff ? 'checked' : ''}`}
                            role="button"
                            onClick={toggleOffCheckBox}
                            style={{ pointerEvents: isSickDay ? 'none' : 'unset' }}
                        />
                        <span className="text-lg mr-3">Day off</span>
                    </div>
                </div>
                <div className="row sick-day-checkbox mt-0">
                    <div className={`col-9 switch-container ${isDayOff ? 'disabled' : ''}`}>
                        <div
                            className={`switch ${isSickDay ? 'checked' : ''}`}
                            role="button"
                            onClick={toggleSickCheckBox}
                            style={{ pointerEvents: isDayOff ? 'none' : 'unset' }}
                        />
                        <span className="text-lg sick-text">Sick Day</span>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default WorkingHours;

